const KALZEN_REVEAL_SELECTOR = '.kz-reveal';

function revealImmediately(root = document) {
  root.querySelectorAll(KALZEN_REVEAL_SELECTOR).forEach((element) => element.classList.add('is-visible'));
}

function initializeReveals(root = document) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
    revealImmediately(root);
    return;
  }

  if (!window.kalzenRevealObserver) {
    window.kalzenRevealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.14, rootMargin: '0px 0px -7% 0px' }
    );
  }

  root.querySelectorAll(`${KALZEN_REVEAL_SELECTOR}:not(.is-visible)`).forEach((element) => {
    window.kalzenRevealObserver.observe(element);
  });
}

function activateMode(switcher, button) {
  const tabs = Array.from(switcher.querySelectorAll('[role="tab"]'));
  const panels = Array.from(switcher.querySelectorAll('[role="tabpanel"]'));
  const panelId = button.getAttribute('aria-controls');

  tabs.forEach((tab) => {
    const active = tab === button;
    tab.setAttribute('aria-selected', active ? 'true' : 'false');
    tab.tabIndex = active ? 0 : -1;
  });

  panels.forEach((panel) => {
    panel.hidden = panel.id !== panelId;
  });

  switcher.dataset.activeMode = button.dataset.mode || '1';

  const voiceDemo = switcher.querySelector('[data-kz-voice-demo]');
  if (voiceDemo) applyVoicePreset(voiceDemo, button.dataset.kzVoicePreset || 'comfort');
}

const KALZEN_VOICE_PRESETS = {
  original: { gain: 1, lowpass: 20000 },
  comfort: { gain: 0.50, lowpass: 7500 },
  light: { gain: 0.126, lowpass: 6000 },
  deep: { gain: 0.071, lowpass: 3800 }
};

function voicePresetLabel(preset) {
  if (preset === 'original') return 'Original voice';
  return `${preset.charAt(0).toUpperCase()}${preset.slice(1)} simulation`;
}

function applyVoicePreset(demo, presetName) {
  const preset = KALZEN_VOICE_PRESETS[presetName] || KALZEN_VOICE_PRESETS.comfort;
  const now = demo.kzAudioContext?.currentTime || 0;
  const audio = demo.querySelector('[data-kz-voice-audio]');

  demo.dataset.voicePreset = presetName;

  if (demo.kzGainNode && demo.kzLowpassNode) {
    demo.kzGainNode.gain.cancelScheduledValues(now);
    demo.kzGainNode.gain.setTargetAtTime(preset.gain, now, 0.06);
    demo.kzLowpassNode.frequency.cancelScheduledValues(now);
    demo.kzLowpassNode.frequency.setTargetAtTime(preset.lowpass, now, 0.06);
  } else if (audio) {
    audio.volume = preset.gain;
  }

  const originalButton = demo.querySelector('[data-kz-voice-original]');
  if (originalButton) originalButton.setAttribute('aria-pressed', presetName === 'original' ? 'true' : 'false');

  const status = demo.querySelector('[data-kz-voice-status]');
  if (status) status.textContent = `${voicePresetLabel(presetName)} selected.`;
}

function stopVoiceMeter(demo) {
  if (demo.kzMeterFrame) cancelAnimationFrame(demo.kzMeterFrame);
  demo.kzMeterFrame = null;
  demo.querySelectorAll('[data-kz-voice-meter] span').forEach((bar) => {
    bar.style.removeProperty('--kz-meter-level');
  });
}

function startVoiceMeter(demo) {
  const analyser = demo.kzAnalyserNode;
  const bars = Array.from(demo.querySelectorAll('[data-kz-voice-meter] span'));
  if (!analyser || !bars.length) return;

  const frequencyData = new Uint8Array(analyser.frequencyBinCount);
  const draw = () => {
    if (demo.querySelector('[data-kz-voice-audio]')?.paused) {
      stopVoiceMeter(demo);
      return;
    }

    analyser.getByteFrequencyData(frequencyData);
    const usefulBins = Math.max(1, Math.floor(frequencyData.length * 0.32));
    bars.forEach((bar, index) => {
      const bin = Math.min(usefulBins - 1, Math.floor((index / bars.length) * usefulBins));
      const level = Math.max(0.08, frequencyData[bin] / 255);
      bar.style.setProperty('--kz-meter-level', level.toFixed(3));
    });
    demo.kzMeterFrame = requestAnimationFrame(draw);
  };

  stopVoiceMeter(demo);
  draw();
}

function prepareVoiceAudio(demo) {
  if (demo.kzAudioContext) return demo.kzAudioContext;
  if (demo.kzAudioUnavailable) return null;

  const audio = demo.querySelector('[data-kz-voice-audio]');
  const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
  if (!audio || !AudioContextConstructor) return null;

  try {
    audio.volume = 1;
    const context = new AudioContextConstructor();
    const source = context.createMediaElementSource(audio);
    const lowpass = context.createBiquadFilter();
    const gain = context.createGain();
    const analyser = context.createAnalyser();

    lowpass.type = 'lowpass';
    lowpass.Q.value = 0.72;
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.8;

    source.connect(lowpass);
    lowpass.connect(gain);
    gain.connect(analyser);
    analyser.connect(context.destination);

    demo.kzAudioContext = context;
    demo.kzLowpassNode = lowpass;
    demo.kzGainNode = gain;
    demo.kzAnalyserNode = analyser;
    applyVoicePreset(demo, demo.dataset.voicePreset || 'comfort');

    return context;
  } catch (error) {
    demo.kzAudioUnavailable = true;
    applyVoicePreset(demo, demo.dataset.voicePreset || 'comfort');
    return null;
  }
}

function setVoicePlaybackState(demo, isPlaying) {
  const toggle = demo.querySelector('[data-kz-voice-toggle]');
  const label = demo.querySelector('[data-kz-voice-toggle-label]');
  if (!toggle || !label) return;

  toggle.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
  toggle.setAttribute('aria-label', isPlaying ? 'Pause voice comparison' : 'Play voice comparison');
  label.textContent = isPlaying ? demo.dataset.pauseLabel : demo.dataset.playLabel;
  demo.classList.toggle('is-playing', isPlaying);
}

function initializeVoiceDemos(root = document) {
  root.querySelectorAll('[data-kz-voice-demo]:not([data-kz-voice-ready])').forEach((demo) => {
    demo.dataset.kzVoiceReady = 'true';
    const audio = demo.querySelector('[data-kz-voice-audio]');
    const toggle = demo.querySelector('[data-kz-voice-toggle]');
    const originalButton = demo.querySelector('[data-kz-voice-original]');
    if (!audio || !toggle) return;

    const initialLabel = demo.querySelector('[data-kz-voice-toggle-label]')?.textContent.trim() || 'Play voice';
    demo.dataset.playLabel = initialLabel;
    demo.dataset.pauseLabel = demo.closest('[data-kz-mode-switcher]')?.dataset.voicePauseLabel || 'Pause voice';
    applyVoicePreset(demo, demo.dataset.voicePreset || 'comfort');

    toggle.addEventListener('click', async () => {
      const context = prepareVoiceAudio(demo);
      if (context?.state === 'suspended') await context.resume();

      if (audio.paused) {
        try {
          document.querySelectorAll('[data-kz-voice-audio]').forEach((otherAudio) => {
            if (otherAudio !== audio) otherAudio.pause();
          });
          await audio.play();
        } catch (error) {
          const status = demo.querySelector('[data-kz-voice-status]');
          if (status) status.textContent = 'The voice sample could not be played. Check the audio file link.';
        }
      } else {
        audio.pause();
      }
    });

    originalButton?.addEventListener('click', () => applyVoicePreset(demo, 'original'));
    audio.addEventListener('play', () => {
      setVoicePlaybackState(demo, true);
      startVoiceMeter(demo);
    });
    audio.addEventListener('pause', () => {
      setVoicePlaybackState(demo, false);
      stopVoiceMeter(demo);
    });
    audio.addEventListener('ended', () => {
      setVoicePlaybackState(demo, false);
      stopVoiceMeter(demo);
    });
  });
}

function initializeModeSwitchers(root = document) {
  root.querySelectorAll('[data-kz-mode-switcher]:not([data-kz-ready])').forEach((switcher) => {
    switcher.dataset.kzReady = 'true';
    const tabs = Array.from(switcher.querySelectorAll('[role="tab"]'));

    tabs.forEach((button, index) => {
      button.addEventListener('click', () => activateMode(switcher, button));
      button.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        let nextIndex = index;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;

        tabs[nextIndex].focus();
        activateMode(switcher, tabs[nextIndex]);
      });
    });
  });
}

function initializeKalzen(root = document) {
  initializeReveals(root);
  initializeModeSwitchers(root);
  initializeVoiceDemos(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeKalzen());
} else {
  initializeKalzen();
}

document.addEventListener('shopify:section:load', (event) => initializeKalzen(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-kz-voice-demo]').forEach((demo) => {
    stopVoiceMeter(demo);
    demo.querySelector('[data-kz-voice-audio]')?.pause();
    demo.kzAudioContext?.close();
  });
});
